import classNames from 'classnames';
import './AnswerDetail.scss';
import { AnswerDetailValuesFunctions } from './AnswerDetailValuesFunctions';

export default function AnswerDetailValues({ question, inspection }) {
  if (question?.answerType !== 'quantitative_input') {
    return null;
  }
  const rawAnswersArray = AnswerDetailValuesFunctions.getAnswerValuesArray(
    question,
    inspection,
  );
  const answerValuesArray = AnswerDetailValuesFunctions.convertRawAnswers(
    rawAnswersArray,
    question,
  );

  if (!answerValuesArray?.length) {
    return null;
  }

  return (
    <>
      {(() => {
        let lastAnswerIndex = 0;
        let dimIdx = -1;

        return answerValuesArray?.map((av, avIdx) => {
          const tv = AnswerDetailValuesFunctions.getTargetValueData(
            question,
            inspection,
            av,
            answerValuesArray,
          );
          const isDateAnswer = av.answerExpectedMeasureTableResult === 'Date';
          const diff = isDateAnswer
            ? null
            : AnswerDetailValuesFunctions.getAnswerDeviation(av, tv);
          const dateDiff = isDateAnswer
            ? AnswerDetailValuesFunctions.getDateAnswerDeviation(av, tv)
            : null;

          if (av.answerIndex === lastAnswerIndex) {
            dimIdx++;
          } else {
            dimIdx = 0;
          }

          lastAnswerIndex = av.answerIndex;

          const renderDimensionLabel = () =>
            av.answerExpectedMeasureTableResult === 'Dimensions' ? (
              <>
                <span className='answer-dimension'>
                  {av.answerExpectedMeasureTableResultDimension}
                </span>
                <span className='answer-dimension-equals'> = </span>
              </>
            ) : null;

          const renderAnswerValue = () => {
            const displayValue = tv.answerValueConverted ?? av.answerValue;
            return (
              <span className='answer-value'>
                {Number.isFinite(displayValue) ? parseFloat(displayValue.toFixed(2)) : displayValue}
              </span>
            );
          };

          const renderAnswerUom = () => {
            const answerUom = tv.answerValueConverted != null
              ? tv.targetValueUnit
              : (av.answerUnit ?? tv.targetValueUnit);
            return answerUom?.length ? (
              <span className='answer-uom'>{answerUom}</span>
            ) : null;
          };

          const renderAnswerDeviation = () => {
            let output = null;
            const displayUnit = tv.answerValueConverted != null
              ? tv.targetValueUnit
              : (av.answerUnit ?? tv.targetValueUnit);
            const fmtTarget = Number.isFinite(tv.targetValue)
              ? parseFloat(tv.targetValue.toFixed(2))
              : tv.targetValue;

            if (Number.isFinite(diff.dAbsolute)) {
              if (diff.dAbsolute === 0) {
                // Exact match - still show expected value
                output = (
                  <span className='answer-deviation'>
                    (0/0% of expected {fmtTarget}
                    {displayUnit})
                  </span>
                );
              } else {
                const diffSign = diff.dAbsolute > 0 ? '+' : '-';
                const fmtAbsolute = parseFloat(Math.abs(diff.dAbsolute).toFixed(2));
                const fmtPercentage = Number.isFinite(diff.dPercentage)
                  ? parseFloat(diff.dPercentage.toFixed(2))
                  : null;

                output = (
                  <span
                    className={classNames('answer-deviation', {
                      'exceeds-tolerance': !!diff.exceedsTolerance,
                    })}
                  >
                    ({diffSign}
                    {fmtAbsolute}
                    {displayUnit}
                    {fmtPercentage != null && `/${fmtPercentage}%`}
                    {' of expected '}
                    {fmtTarget}
                    {displayUnit})
                  </span>
                );
              }
            }

            return output;
          };

          const renderDateDeviation = () => {
            if (!dateDiff?.expectedDisplay) {
              return null;
            }

            const { dDays, expectedDisplay, exceedsTolerance } = dateDiff;
            const diffLabel =
              dDays === 0
                ? '0 days'
                : `${dDays > 0 ? '+' : '-'}${Math.abs(dDays)} ${
                    Math.abs(dDays) === 1 ? 'day' : 'days'
                  }`;

            return (
              <span
                className={classNames('answer-deviation', {
                  'exceeds-tolerance': exceedsTolerance,
                })}
              >
                ({diffLabel} of expected {expectedDisplay})
              </span>
            );
          };

          const output = (
            <div className='answer-value-item' key={avIdx}>
              {renderDimensionLabel()}
              {renderAnswerValue()}
              {renderAnswerUom()}{' '}
              {isDateAnswer ? renderDateDeviation() : renderAnswerDeviation()}
            </div>
          );

          return output;
        });
      })()}
    </>
  );
}
